import { getEffectiveDependencyIds } from '../../shared/nestDependencyInfo';
import {
  getAllConditionTaskIds,
  addTaskToConditions,
  replaceTaskInConditions,
} from '../../shared/ConditionsDropdown/utils/conditionHelpers';
import { findConnectedNodes } from '../../FlowchartView/hooks/utils/graphTraversal';
import {
  expandDependsOnReplacing,
  externalDependencyIdsToDependsOn,
} from './nestTaskHelpers';

const NEST_LAYOUT_ANCHOR = 100;

function depId(dep) {
  return typeof dep === 'string' ? dep : dep?.taskId;
}

/** True if task.dependsOn or unlock conditions reference id (matches how edges are stored). */
function taskReferencesTaskId(task, id) {
  if (Array.isArray(task.dependsOn) && task.dependsOn.some((d) => depId(d) === id)) return true;
  return getAllConditionTaskIds(task.unlockConditions).includes(id);
}

/**
 * True if this task has an incoming edge from inside the nested set (some inner task is its
 * prerequisite). That is what defines an entry/source: NOT this.
 *
 * Previous code inverted this: it checked whether any inner task *depended on* t, which is true
 * for merge/sink nodes (many things point into them), so upstream from the parent was wrongly
 * merged into sinks — e.g. 1 → Y and Z instead of 1 → A and B.
 */
function hasPrerequisiteFromInnerSet(task, innerSet) {
  return getEffectiveDependencyIds(task).some((id) => innerSet.has(id));
}

function computeSinkNodes(innerTasks, innerSet) {
  return innerTasks.filter(
    (t) => !innerTasks.some((w) => innerSet.has(w.id) && getEffectiveDependencyIds(w).includes(t.id)),
  );
}

/**
 * Inverse of compile's wrapper edge: every inner source gets the wrapper's upstream prerequisites
 * (same ids/order as wrapper.dependsOn when present), then its inner-only deps — matching
 * U → wrapper → inner entry structure.
 */
function mergeUpstreamFromWrapperIntoSource(sourceTask, wrapperTask, innerSet) {
  const upstreamIds = getEffectiveDependencyIds(wrapperTask).filter((id) => id && !innerSet.has(id));
  if (upstreamIds.length === 0) return { ...sourceTask };

  const condIds = getAllConditionTaskIds(sourceTask.unlockConditions);
  if (condIds.length > 0) {
    let uc = sourceTask.unlockConditions;
    for (const id of upstreamIds) {
      uc = addTaskToConditions(uc, id);
    }
    return { ...sourceTask, unlockConditions: uc };
  }

  const innerDeps = (Array.isArray(sourceTask.dependsOn) ? sourceTask.dependsOn : []).filter((dep) => {
    const id = depId(dep);
    return id && innerSet.has(id);
  });

  const wrapperDepById = new Map();
  if (Array.isArray(wrapperTask.dependsOn)) {
    for (const dep of wrapperTask.dependsOn) {
      const id = depId(dep);
      if (id) wrapperDepById.set(id, dep);
    }
  }

  const seen = new Set();
  const upstreamDeps = [];
  for (const id of upstreamIds) {
    if (seen.has(id)) continue;
    seen.add(id);
    const dep = wrapperDepById.get(id) ?? externalDependencyIdsToDependsOn(new Set([id]), wrapperTask)[0];
    upstreamDeps.push(dep);
  }

  return { ...sourceTask, dependsOn: [...upstreamDeps, ...innerDeps] };
}

function patchOuterTaskForExplode(task, wrapperId, exitIds, innerSet) {
  if (innerSet.has(task.id)) return task;
  if (!taskReferencesTaskId(task, wrapperId)) return task;

  let patched = { ...task };

  if (Array.isArray(patched.dependsOn)) {
    const hasWrapper = patched.dependsOn.some((dep) => depId(dep) === wrapperId);
    if (hasWrapper) {
      patched.dependsOn = expandDependsOnReplacing(patched.dependsOn, wrapperId, exitIds);
    }
  }

  if (patched.unlockConditions && getAllConditionTaskIds(patched.unlockConditions).includes(wrapperId)) {
    let uc = replaceTaskInConditions(patched.unlockConditions, wrapperId, exitIds[0]);
    for (let i = 1; i < exitIds.length; i++) {
      uc = addTaskToConditions(uc, exitIds[i]);
    }
    patched.unlockConditions = uc;
  }

  return patched;
}

/**
 * Inline a wrapper mission's nested campaign into the parent campaign graph.
 * Removes the wrapper task, lifts nested tasks to the parent, rewires upstream/downstream edges,
 * trashes the nested sprint, and reparents child nested sprints to the parent campaign.
 *
 * Nested sprint rows in `allSprints` are often shallow (no `tasks`) until the user opens that
 * campaign or something calls `loadFullSprint`. We load on demand so Explode works without
 * visiting the nested campaign first.
 */
export async function runExplodeNestedCampaign({
  wrapperTaskId,
  tasks,
  nestedSprints,
  allSprints = [],
  localSprint,
  setLocalSprint,
  onAddSprint,
  onUpdateSprint,
  recordAction,
  onTrashSprint,
  flowchartRef,
  loadFullSprint,
}) {
  if (!wrapperTaskId || !Array.isArray(tasks)) return;

  const wrapperTask = tasks.find((t) => t.id === wrapperTaskId);
  if (!wrapperTask?.nestedSprintId) return;

  const nsId = wrapperTask.nestedSprintId;
  let nestedSprint =
    nestedSprints.find((ns) => ns.id === nsId) || allSprints.find((s) => s.id === nsId);
  if (!nestedSprint) return;

  const needsLoad =
    typeof loadFullSprint === 'function' &&
    (!Array.isArray(nestedSprint.tasks) || nestedSprint.tasks.length === 0);

  if (needsLoad) {
    try {
      const loaded = await loadFullSprint(nsId);
      if (loaded && Array.isArray(loaded.tasks) && loaded.tasks.length > 0) {
        nestedSprint = loaded;
      }
    } catch (e) {
      console.error('[runExplodeNestedCampaign] loadFullSprint failed', e);
      return;
    }
  }

  const innerTasks = [...(nestedSprint.tasks || [])];
  if (innerTasks.length === 0) return;

  const originalTasks = tasks.map((t) => ({ ...t }));
  const nestedSprintSnapshot = { ...nestedSprint, tasks: (nestedSprint.tasks || []).map((t) => ({ ...t })) };

  const innerSet = new Set(innerTasks.map((t) => t.id));

  const sources = innerTasks.filter((t) => !hasPrerequisiteFromInnerSet(t, innerSet));
  const sourceIds = new Set(sources.map((s) => s.id));

  let sinks = computeSinkNodes(innerTasks, innerSet);
  if (sinks.length === 0) {
    sinks = [...innerTasks];
  }
  const exitIds = sinks.map((s) => s.id);

  const wx = wrapperTask.x ?? 0;
  const wy = wrapperTask.y ?? 0;

  const liftedTasks = innerTasks.map((t) => {
    const ix = t.x ?? NEST_LAYOUT_ANCHOR;
    const iy = t.y ?? NEST_LAYOUT_ANCHOR;
    let next = {
      ...t,
      x: wx + (ix - NEST_LAYOUT_ANCHOR),
      y: wy + (iy - NEST_LAYOUT_ANCHOR),
    };
    if (sourceIds.has(t.id)) {
      next = mergeUpstreamFromWrapperIntoSource(next, wrapperTask, innerSet);
    }
    return next;
  });

  const patchedOuter = tasks
    .filter((t) => t.id !== wrapperTaskId)
    .map((t) => patchOuterTaskForExplode(t, wrapperTaskId, exitIds, innerSet));

  const remainingTasks = [...patchedOuter, ...liftedTasks];

  const grandchildSprints = (allSprints || []).filter((s) => s.parentSprintId === nestedSprint.id);
  const originalGrandchildParents = {};
  grandchildSprints.forEach((s) => {
    originalGrandchildParents[s.id] = s.parentSprintId;
  });

  if (grandchildSprints.length > 0 && onUpdateSprint) {
    grandchildSprints.forEach((s) => {
      onUpdateSprint({
        ...s,
        tasks: s.tasks || [],
        categories: s.categories || [],
        parentSprintId: localSprint.id,
      });
    });
  }

  setLocalSprint((prev) => {
    if (!prev) return prev;
    return {
      ...prev,
      tasks: remainingTasks,
    };
  });

  if (onTrashSprint) {
    onTrashSprint(nestedSprint.id);
  }

  const nestedSprintIdsInLifted = new Set(
    liftedTasks.map((t) => t.nestedSprintId).filter(Boolean),
  );

  if (nestedSprintIdsInLifted.size > 0 && onUpdateSprint) {
    nestedSprintIdsInLifted.forEach((childId) => {
      const childSprint = allSprints.find((ns) => ns.id === childId);
      if (childSprint) {
        onUpdateSprint({
          ...childSprint,
          tasks: childSprint.tasks || [],
          categories: childSprint.categories || [],
          parentSprintId: localSprint.id,
        });
      }
    });
  }

  const connectedBefore = findConnectedNodes(new Set([wrapperTaskId]), tasks);
  const parentTidySeedIds = new Set();
  exitIds.forEach((id) => parentTidySeedIds.add(id));
  connectedBefore.forEach((id) => {
    if (id !== wrapperTaskId && !innerSet.has(id)) parentTidySeedIds.add(id);
  });
  const connectedAfter = findConnectedNodes(parentTidySeedIds, remainingTasks);
  // Same pattern as insert-on-edge tidy (useFlowchartCoreNodeActions): clear runtime overrides
  // for the affected subgraph, then partial autoLayout + chain compaction. The ref must expose
  // clearCustomPositionsForNodeIds so tidy recalculates from scratch.
  const clearIds = new Set(connectedAfter);
  clearIds.add(wrapperTaskId);
  flowchartRef?.current?.clearCustomPositionsForNodeIds?.(clearIds);
  if (connectedAfter.size > 0) {
    setTimeout(() => {
      flowchartRef?.current?.autoLayout?.(connectedAfter, { skipRecordAction: true });
    }, 50);
  }

  if (recordAction) {
    const name = wrapperTask.name || 'Mission';
    const description = `Explode nested: "${name}"`;
    recordAction({
      type: 'EXPLODE_NESTED_CAMPAIGN',
      description,
      undo: () => {
        if (onAddSprint) {
          onAddSprint(nestedSprintSnapshot);
        } else if (onUpdateSprint) {
          onUpdateSprint({
            ...nestedSprintSnapshot,
            tasks: nestedSprintSnapshot.tasks || [],
            categories: nestedSprintSnapshot.categories || [],
          });
        }
        setLocalSprint((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            tasks: originalTasks.map((t) => ({ ...t })),
          };
        });
        grandchildSprints.forEach((s) => {
          const originalParentId = originalGrandchildParents[s.id];
          if (originalParentId != null && onUpdateSprint) {
            onUpdateSprint({
              ...s,
              tasks: s.tasks || [],
              categories: s.categories || [],
              parentSprintId: originalParentId,
            });
          }
        });
      },
      redo: () => {
        setLocalSprint((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            tasks: remainingTasks.map((t) => ({ ...t })),
          };
        });
        if (onTrashSprint) {
          onTrashSprint(nestedSprint.id);
        }
        if (grandchildSprints.length > 0 && onUpdateSprint) {
          grandchildSprints.forEach((s) => {
            onUpdateSprint({
              ...s,
              tasks: s.tasks || [],
              categories: s.categories || [],
              parentSprintId: localSprint.id,
            });
          });
        }
        nestedSprintIdsInLifted.forEach((childId) => {
          const childSprint = allSprints.find((ns) => ns.id === childId);
          if (childSprint && onUpdateSprint) {
            onUpdateSprint({
              ...childSprint,
              tasks: childSprint.tasks || [],
              categories: childSprint.categories || [],
              parentSprintId: localSprint.id,
            });
          }
        });
      },
    });
  }
}
